import BaseDrawer from '@c/drawers/BaseDrawer';
import { CartIcon, CloseIcon } from '@c/icons';
import Button from '@ui/Button';
import { formatCurrency, isMobile } from '@util/index';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { ProductDocument } from 'models/product';
import Link from 'next/link';
import { Fragment } from 'react';
import CartProduct from './CartProduct';
import MXUpsellProductCard from './MXUpsellProductCard';
import useUpsell from '@util/hooks/useUpsell';
import { CartItem } from '@util/types/firestore/carts';
interface ShoppingCartProps {}

const ShoppingCart = ({}: ShoppingCartProps) => {
  const { cartOpen, setCartOpen, cart, realTimeProducts } = useShoppingCart();

  const { products: upsellProducts, isLoading: isFetching } = useUpsell({
    product_ids: cart?.product_ids ?? [],
  });

  if (!cart) return null;

  const groupProductsBySeller = (items: CartItem[]) => {
    return items.reduce<Record<string, CartItem[]>>((acc, item) => {
      const sellerId = item.seller_id;
      const sellerProducts = acc[sellerId] || [];
      sellerProducts.push(item);
      acc[sellerId] = sellerProducts;
      return acc;
    }, {});
  };

  const groupedProducts = groupProductsBySeller(cart.items);

  return (
    <BaseDrawer
      show={cartOpen}
      position={'right'}
      dismiss={() => setCartOpen(false)}
      fill={isMobile() ? true : false}
    >
      <>
        <div className="relative flex h-full w-full flex-col bg-white">
          <div className="relative flex w-full border-b  border-zinc-200">
            <div className="flex h-[6rem] w-full items-center justify-center">
              <h1 className="text-[1.8rem] font-semibold">YOUR CART</h1>
            </div>

            <button
              onClick={() => setCartOpen(false)}
              className="absolute right-[2rem] top-[1.7rem]"
            >
              <CloseIcon width={20} height={20} />
            </button>
          </div>

          <div
            className={`flex grow flex-col overflow-y-auto ${
              isMobile() && cart.items.length > 1 && 'pb-48'
            }`}
          >
            {!!cart.items.length ? (
              <div className="flex flex-col gap-y-12 px-8 py-12">
                {Object.values(groupedProducts).map((products, index) => (
                  <Fragment key={index}>
                    {products.map((item) => {
                      const product = realTimeProducts.find(
                        (p) => p.id === item.product_id
                      );
                      if (!product) return null;
                      return (
                        <CartProduct
                          key={item.product_id}
                          product={product}
                          sidebar={true}
                        />
                      );
                    })}
                    <hr className="-mx-8 text-zinc-200" />
                  </Fragment>
                ))}

                <div className="flex flex-col gap-y-6">
                  {isFetching || isFetching ? (
                    <div className="pt- flex w-full flex-col gap-y-6 px-8">
                      <h3 className="text-[1.8rem] font-semibold text-zinc-800">
                        You may also need
                      </h3>
                      <div className="flex flex-nowrap overflow-x-scroll p-4 scrollbar-none">
                        <div className="flex min-w-[75vw] items-center justify-between gap-4 rounded-[3rem] pr-2 pt-2 sm:w-full sm:min-w-0">
                          <div className="h-[10rem] w-[10rem] animate-pulse rounded-[3rem] bg-gray-500" />

                          <div className="flex grow flex-col gap-y-1">
                            <h3 className="line-clamp-2 h-[2.2rem] w-[10rem] animate-pulse whitespace-pre-wrap rounded-lg bg-gray-500 text-[1.6rem]"></h3>

                            <p
                              className={
                                'h-[2.2rem] w-[5rem] animate-pulse rounded-lg bg-gray-500 text-[1.6rem] font-bold'
                              }
                            ></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : upsellProducts?.length ? (
                    <div className="flex w-full flex-col gap-y-6 px-8 pt-8">
                      <h3 className="text-[2.1rem] font-semibold text-zinc-800 sm:text-[1.8rem]">
                        You may also need
                      </h3>

                      <UpsellProducts upsellProducts={upsellProducts} />
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="mb-48 flex h-full w-full items-center justify-center sm:mb-0 sm:h-[70rem]">
                <div className="flex flex-col items-center gap-y-8">
                  <CartIcon className="h-32 w-32 text-zinc-400" />

                  <h3 className="text-[1.8rem] font-medium text-black">
                    Your cart is empty!
                  </h3>

                  <Button
                    text="Continue Shopping"
                    className="!rounded-[2rem] !py-12 text-[1.8rem] sm:rounded-lg sm:py-4"
                    type="secondary"
                    href={'/'}
                    onClick={() => setCartOpen(false)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 flex w-full flex-col items-center justify-center gap-y-6 bg-white/60 px-8 py-4 backdrop-blur-lg">
            <div className="flex w-full flex-col gap-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[1.8rem] text-gray-500">Subtotal</span>

                <span className="text-[1.8rem] font-semibold text-black">
                  {formatCurrency(cart.items.length ? cart.subtotal : 0)}
                </span>
              </div>

              <Link
                href={'/checkout'}
                className="flex w-full items-center justify-center rounded-[2rem] bg-[#C41618] py-6 text-[2rem] font-semibold text-white"
                onClick={() => {
                  setCartOpen(false);
                }}
              >
                <div className="flex items-center gap-x-2">
                  <span>Continue to checkout</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </>
    </BaseDrawer>
  );
};

const UpsellProducts = ({
  upsellProducts,
}: {
  upsellProducts: ProductDocument[] | undefined;
}) => {
  if (!upsellProducts || !upsellProducts.length) return;
  return (
    <div className="flex items-center gap-x-8 overflow-x-scroll">
      {upsellProducts.length > 0 ? (
        upsellProducts.map((product) => {
          return (
            <div className="flex-shrink-0" key={product.id}>
              <MXUpsellProductCard size={'small'} product={product} isSidebar />
            </div>
          );
        })
      ) : (
        <div className="flex w-full items-center justify-center pt-8">
          <h3 className="text-[1.8rem] font-medium text-zinc-600">
            No products available
          </h3>
        </div>
      )}
    </div>
  );
};

export default ShoppingCart;
